import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { STATS, LOCATIONS_LIVE, LOCATIONS_SOON } from "../../data";

const Counter = ({ target, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const end = parseInt(target, 10);
    let cur = 0;
    const step = Math.max(1, Math.ceil(end / 40));
    const t = setInterval(() => {
      cur += step;
      if (cur >= end) {
        cur = end;
        clearInterval(t);
      }
      setN(cur);
    }, 28);
    return () => clearInterval(t);
  }, [inView, target]);

  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
};

export const Coverage = () => {
  return (
    <section
      id="network"
      data-testid="network"
      className="relative overflow-hidden py-28 md:py-40"
    >
      {/* Giant outlined background text */}
      <div className="pointer-events-none absolute inset-0 flex items-center overflow-hidden">
        <Marquee speed={30} gradient={false} autoFill>
          <span className="px-8 font-display text-[22vw] font-black uppercase leading-none text-stroke opacity-40">
            Pan India ·
          </span>
        </Marquee>
      </div>

      <div className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="mb-16 max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--volt)]">
            / The Network
          </span>
          <h2 className="mt-4 font-display text-4xl font-bold uppercase tracking-tight text-white md:text-6xl">
            One country.
            <br />
            One standard.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-white/60 md:text-lg">
            Four centres live across Punjab today, two more opening in Noida
            and Jammu &amp; Kashmir — and a roadmap to 250+ CARFIX bays across
            India.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 md:grid-cols-4">
          {STATS.map((s) => (
            <div
              key={s.label}
              data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}
              className="bg-[#050505] p-8 md:p-10"
            >
              <div className="font-display text-5xl font-black text-white md:text-7xl">
                <Counter target={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-2">
          <div data-testid="locations-live">
            <div className="mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[var(--volt)]">
              <span className="h-2 w-2 rounded-full bg-[var(--volt)]" />
              Now Open
            </div>
            <div className="flex flex-col divide-y divide-white/10 border-t border-white/10">
              {LOCATIONS_LIVE.map((l, i) => (
                <motion.div
                  key={l.city}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.5 }}
                  className="flex items-center justify-between py-4"
                >
                  <span className="font-display text-xl font-bold text-white md:text-2xl">
                    {l.city}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-wider text-white/40">
                    {l.state}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          <div data-testid="locations-soon">
            <div className="mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-white/50">
              <span className="h-2 w-2 rounded-full border border-white/50" />
              Opening Soon
            </div>
            <div className="flex flex-col divide-y divide-white/10 border-t border-white/10">
              {LOCATIONS_SOON.map((l, i) => (
                <motion.div
                  key={l.city}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.5 }}
                  className="flex items-center justify-between py-4"
                >
                  <span className="font-display text-xl font-bold text-white/70 md:text-2xl">
                    {l.city}
                  </span>
                  <span className="rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-white/50">
                    Soon
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
